"use client"

import styled from "@emotion/styled"
import { GetArticlesQuery, GetPageByRouteQuery } from "@eurodrive/apollo"
import { Articles, BlockRenderer, CustomBreadcrumbs } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { extractProperty, getBreadCrumbs } from "@eurodrive/utils"
import { IconButton, Typography } from "@mui/material"
import Image from "next/image"
import ArticleImageBlock from "./ArticleImageBlock"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
    padding-bottom: 64px;
  }
`

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    max-width: 800px;
    margin: 0 auto;
  }
`

const IntroBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const DetailsRow = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f5f6f8;
  border-bottom: 1px solid #f5f6f8;
`

const StyledIconButton = styled(IconButton)`
  padding: 16px;
`

const formatDate = (dateString: string): string => {
  if (!dateString) return ""

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString // Return original if invalid date

  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

const components = {
  titleAndText: () => import("./TitleAndText"),
  articleImageBlock: () => import("./ArticleImageBlock"),
}
export default function ArticlePage({
  data,
  locale,
  articles,
}: {
  data: GetPageByRouteQuery
  locale: string
  articles: GetArticlesQuery
}) {
  const breadCrumbs = getBreadCrumbs(data, locale)
  const title = extractProperty(data.page, "title")
  const description = extractProperty(data.page, "description")
  const date = extractProperty(data.page, "date")
  const mainImage = extractProperty(data.page, "mainImage")?.[0]
  const articleContentRaw = extractProperty(data.page, "articleContent")
  const articleContent = Array.isArray(articleContentRaw) ? articleContentRaw : []
  const moreArticleBlocks = extractProperty(data.page, "moreArticleBlock")?.[0]
  const formattedDate = formatDate(date)
  const handleShare = async () => {
    const currentUrl = window.location.href

    if (navigator.share) {
      try {
        await navigator.share({
          title: title || "מאמר",
          text: description || "",
          url: currentUrl,
        })
      } catch (error) {
        console.log("Error sharing:", error)
      }
    } else {
      try {
        await navigator.clipboard.writeText(currentUrl)
      } catch (error) {
        console.log("Error copying to clipboard:", error)
      }
    }
  }

  return (
    <>
      <Container paddingMobile>
        <StyledLayout>
          <CustomBreadcrumbs breadcrumbs={breadCrumbs} />
          <MainContainer>
            <IntroBox>
              <Typography variant="h1" component="h1">{title}</Typography>
              <Typography variant="body1">{description}</Typography>
              <DetailsRow>
                <Typography>עודכן בתאריך {formattedDate}</Typography>
                <StyledIconButton onClick={handleShare}>
                  <Image
                    src="/icons/shareButton.svg"
                    width={24}
                    height={24}
                    alt="שתף מאמר"
                  />
                </StyledIconButton>
              </DetailsRow>
            </IntroBox>
            <ArticleImageBlock data={mainImage} />
            {articleContent.map((block: any, index: number) => {
              return (
                <BlockRenderer
                  key={index}
                  blockType={block.blockType}
                  blockData={block}
                  components={components}
                  locale={locale}
                />
              )
            })}
          </MainContainer>
        </StyledLayout>
      </Container>

      <Articles
        data={moreArticleBlocks}
        articles={articles}
        noBackgroundColor
      />
    </>
  )
}
